import { Link } from "react-router-dom";
import { GraduationCap, ArrowRight, ShieldCheck, ArrowLeft, Lock } from "lucide-react";
import PortalHeader from "../components/PortalHeader";
import PortalFooter from "../components/PortalFooter";

export default function Landing() {
  return (
    <div className="min-h-screen bg-stone-50 flex flex-col font-sans">
      
      {/* 1. THE TOP UTILITY BAR (Enterprise Standard for Cross-Domain Routing) */}
      <div className="w-full bg-stone-900 text-stone-300 py-2 px-4 md:px-8 text-xs font-medium flex justify-between items-center z-50">
        <a 
          href="https://gbpiet.ac.in" 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-stone-500 rounded-sm px-1"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Return to Main Website
        </a>
        <div className="hidden md:flex items-center gap-1.5 text-stone-400">
          <Lock className="w-3 h-3" />
        </div>
      </div>

      <PortalHeader />

      {/* 2. THE ASYMMETRIC GATEWAY LAYOUT */}
      <main className="max-w-7xl mx-auto px-6 py-12 md:py-20 flex-1 w-full flex flex-col justify-center">
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24 items-center">
          
          {/* LEFT SIDE: Institutional Authority & Branding */}
          <div className="lg:col-span-5 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 border border-indigo-100 rounded-full w-fit mb-6">
              <ShieldCheck className="w-4 h-4 text-indigo-700" />
              <span className="text-xs uppercase tracking-widest text-indigo-900 font-bold">
                Examination Cell · GBPIET
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl text-stone-900 font-extrabold tracking-tight leading-[1.1]">
              Verified Academic Records
            </h1>
            
            <p className="text-base md:text-lg text-stone-600 mt-6 leading-relaxed max-w-md text-justify">
              The secure gateway to access complete semester-wise results and authenticate official printed grade sheets issued by the institute.
            </p>

            <div className="mt-10 pt-8 border-t border-stone-200 hidden md:block">
              <p className="text-sm font-medium text-stone-900">Need technical assistance?</p>
              <p className="text-sm text-stone-500 mt-1">Contact: jsrauthan_cse@gbpiet.ac.in</p>
            </div>
          </div>

          {/* RIGHT SIDE: Action & Routing Cards */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            
            {/* Student Access Card */}
            <Link
              to="/student"
              className="group relative bg-white border border-stone-200 rounded-2xl p-8 md:p-10 hover:border-indigo-600 hover:shadow-xl transition-all duration-300 overflow-hidden focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-4"
            >
              {/* Left Accent Bar */}
              <div className="absolute top-0 left-0 w-1.5 h-full bg-indigo-600 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"></div>
              
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-bold mb-2">
                    For Enrolled Students
                  </p>
                  <h2 className="text-2xl md:text-3xl text-stone-900 font-bold tracking-tight">Student Portal</h2>
                </div>
                <div className="bg-indigo-50 p-3.5 rounded-full group-hover:bg-indigo-100 group-hover:scale-110 transition-all duration-300">
                  <GraduationCap className="w-7 h-7 text-indigo-700" strokeWidth={2} />
                </div>
              </div>
              
              <p className="text-stone-600 text-sm md:text-base mt-4 leading-relaxed max-w-md text-justify">
                View your complete semester-wise SGPA, CGPA, and earned credits. Authenticate securely using your University Roll Number and Date of Birth.
              </p>
              
              <div className="mt-8 flex items-center gap-2 text-indigo-700 font-bold group-hover:gap-3 transition-all">
                Access your results <ArrowRight className="w-5 h-5" />
              </div>
            </Link>

            {/* Document Verifier Card */}
            <Link
              to="/verify"
              className="group relative bg-white border border-stone-200 rounded-2xl p-8 md:p-10 hover:border-emerald-600 hover:shadow-xl transition-all duration-300 overflow-hidden focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-4"
            >
              {/* Left Accent Bar */}
              <div className="absolute top-0 left-0 w-1.5 h-full bg-emerald-600 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"></div>

              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-bold mb-2">
                    For Employers & Institutions
                  </p>
                  <h2 className="text-2xl md:text-3xl text-stone-900 font-bold tracking-tight">Document Verification</h2>
                </div>
                <div className="bg-emerald-50 p-3.5 rounded-full group-hover:bg-emerald-100 group-hover:scale-110 transition-all duration-300">
                  <ShieldCheck className="w-7 h-7 text-emerald-700" strokeWidth={2} />
                </div>
              </div>

              <p className="text-stone-600 text-sm md:text-base mt-4 leading-relaxed max-w-md text-justify">
                Confirm the authenticity of any GBPIET Grade Sheet by entering the printed alphanumeric verification code to prevent document fraud.
              </p>

              <div className="mt-8 flex items-center gap-2 text-emerald-700 font-bold group-hover:gap-3 transition-all">
                Open verification tool <ArrowRight className="w-5 h-5" />
              </div>
            </Link>

          </div>
        </div>
      </main>

      <PortalFooter />
    </div>
  );
}
